/**
 * Which latency or offline policy graded an alert, read off the alert itself.
 *
 * The service resolves it per request and serves three things: `governedBy` says how far
 * the governing policy reaches, `policyId` names it, and the `latencyPolicy` or
 * `offlinePolicy` link is how it is read. Only a component override belongs to an alert row.
 */

import { AlertGovernance, AlertView } from '@netfoundry-ui/shared/model';
import { isEmpty } from 'lodash';

/** Which kind of policy graded an alert, or `''` when the alert does not say. */
export type GoverningPolicyKind = '' | 'latency' | 'offline';

/** The link relation the service serves each kind of governing policy under. */
const POLICY_LINKS: Record<Exclude<GoverningPolicyKind, ''>, string> = {
    latency: 'latencyPolicy',
    offline: 'offlinePolicy',
};

/** The levels a policy can reach, widest to narrowest. See {@link AlertGovernance}. */
const GOVERNANCE_LEVELS: AlertGovernance[] = ['FLEET_DEFAULT', 'NETWORK_DEFAULT', 'COMPONENT_OVERRIDE'];

/**
 * How far the policy that graded this alert reaches.
 *
 * `''` for an alert that says nothing, which is the same answer as the shipped defaults -
 * no policy is known, so there is none to edit, and both want the same button. A whitelist,
 * so a level this console has not heard of degrades to `''` and offers to create the
 * override that sits over it, which is safe because the service refuses a create against a
 * key already covered with a 409 naming the policy.
 */
export function alertGovernance(alert?: AlertView): AlertGovernance {
    const governedBy = alert?.governedBy;
    return GOVERNANCE_LEVELS.find((level) => level === governedBy) || '';
}

/**
 * Whether a policy for this alert's own components governs it, and can be read.
 *
 * This is the question the row button and the dialog both answer from, because a component
 * override is the one policy an alert row may edit. A policy nothing names is not editable:
 * `COMPONENT_OVERRIDE` with neither an id nor a link leaves nothing to `PATCH`, so the row
 * offers to create instead and the service refuses that with a 409 naming the policy.
 */
export function isComponentGoverned(alert?: AlertView): boolean {
    return alertGovernance(alert) === 'COMPONENT_OVERRIDE' && hasGoverningPolicy(alert);
}

/** Whether the alert names the policy that graded it, either way it can be named. */
export function hasGoverningPolicy(alert?: AlertView): boolean {
    return !isEmpty(governingPolicyId(alert)) || !isEmpty(governingPolicyHref(alert));
}

/** The id of the policy that graded this alert, or `''`. */
export function governingPolicyId(alert?: AlertView): string {
    return alert?.policyId || '';
}

/**
 * The href of the policy that graded this alert, of either kind, or `''`.
 *
 * Preferred over the id: the service decides where its own resources live, so following
 * the link it served beats rebuilding a path from an id.
 */
export function governingPolicyHref(alert?: AlertView): string {
    const kind = linkedPolicyKind(alert);
    return kind ? alert?._links?.[POLICY_LINKS[kind]]?.href || '' : '';
}

/**
 * Which kind of policy graded this alert. The link relation decides, because the service does not publish the kind;
 * with only an id, `isOfflineType` decides from the alert type, and without it the kind is unknown.
 */
export function governingPolicyKind(
    alert?: AlertView,
    isOfflineType?: (alertType: string) => boolean
): GoverningPolicyKind {
    const linked = linkedPolicyKind(alert);
    if (linked || !hasGoverningPolicy(alert) || !isOfflineType) {
        return linked;
    }
    return isOfflineType(alert?.alertType || '') ? 'offline' : 'latency';
}

/** The kind named by the link the alert carries, or `''`. */
function linkedPolicyKind(alert?: AlertView): GoverningPolicyKind {
    if (!isEmpty(alert?._links?.[POLICY_LINKS.offline]?.href)) {
        return 'offline';
    }
    return isEmpty(alert?._links?.[POLICY_LINKS.latency]?.href) ? '' : 'latency';
}
